import { equalsIgnoreCase, isValidEmail } from '@cascade/core/utils';
import { useDependents } from '@cascade/react/dependents';
import { useI18n } from '@cascade/react/i18n';
import type { TReasonKeys } from '@cascade/react/i18n';
import { useUser } from '@cascade/react/user';
import { Button, Dialog, Input, Text } from '@cascade/ui-library';
import { useState } from 'react';
import classes from './AddDependentDialog.module.scss';
import type { TAddDependentDialogProps } from './AddDependentDialog.types.ts';

const addDependentReasonKeys = {
  resource_exhausted: 'dependents.add.limit',
} satisfies TReasonKeys;

export const AddDependentDialog = ({
  onAdded,
  onClose,
}: TAddDependentDialogProps) => {
  const { translate, translateError } = useI18n();
  const { user } = useUser();
  const { isAddingDependent, hasDependent, addDependent } = useDependents();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [hasBlurredEmail, setHasBlurredEmail] = useState(false);
  const [addError, setAddError] = useState<unknown>();

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const hasEmail = trimmedEmail.length > 0;
  const isEmailValid = isValidEmail(trimmedEmail);
  const isDuplicate = hasDependent(trimmedEmail);
  const isSelf =
    user?.email !== undefined && equalsIgnoreCase(trimmedEmail, user.email);

  const showEmailInvalid = hasBlurredEmail && hasEmail && !isEmailValid;
  const showEmailDuplicate = isEmailValid && isDuplicate;
  const showEmailSelf = isEmailValid && isSelf;
  const canSubmit =
    isEmailValid && !isDuplicate && !isSelf && !isAddingDependent;

  const hasAddError = addError !== undefined;

  const invalidFeedback = showEmailInvalid
    ? translate('dependents.add.emailInvalid')
    : undefined;

  const duplicateFeedback = showEmailDuplicate
    ? translate('dependents.add.duplicate')
    : undefined;

  const selfFeedback = showEmailSelf
    ? translate('dependents.add.self')
    : undefined;

  const emailFeedback = invalidFeedback ?? selfFeedback ?? duplicateFeedback;

  const addErrorText = translateError(addError, addDependentReasonKeys);

  const changeName = (value: string) => {
    setAddError(undefined);
    setName(value);
  };

  const changeEmail = (value: string) => {
    setAddError(undefined);
    setEmail(value);
  };

  const markEmailBlurred = () => setHasBlurredEmail(true);

  const submit = () => {
    setAddError(undefined);
    addDependent({ name: trimmedName || undefined, email: trimmedEmail })
      .then(() => onAdded(trimmedEmail))
      .catch((error: unknown) => setAddError(error));
  };

  return (
    <Dialog
      open
      size='sm'
      title={translate('dependents.add.title')}
      subtitle={translate('dependents.add.description')}
      dismissible={!isAddingDependent}
      footer={
        <div className={classes.actions}>
          <Button
            variant='secondary'
            label={translate('dependents.add.cancel')}
            disabled={isAddingDependent}
            onClick={onClose}
          />
          <Button
            variant='primary'
            iconType='envelope'
            label={translate('dependents.add.submit')}
            disabled={!canSubmit}
            onClick={submit}
          />
        </div>
      }
      onClose={onClose}
    >
      <div className={classes.fields}>
        <Input
          label={translate('dependents.add.name')}
          value={name}
          disabled={isAddingDependent}
          onChange={changeName}
        />
        <Input
          label={translate('dependents.add.email')}
          type='email'
          value={email}
          required
          disabled={isAddingDependent}
          feedbackLevel={emailFeedback ? 'danger' : undefined}
          feedbackText={emailFeedback}
          onChange={changeEmail}
          onBlur={markEmailBlurred}
        />
        {hasAddError ? (
          <Text
            type='body-2'
            color='text-danger'
            role='alert'
            content={addErrorText}
          />
        ) : null}
      </div>
    </Dialog>
  );
};
